import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';

interface TimeSelectorProps {
    value: string; // HH:mm format
    onChange: (time: string) => void;
    className?: string;
    theme?: 'mystic' | 'dark';
}

export const TimeSelector: React.FC<TimeSelectorProps> = ({ value, onChange, className = '', theme = 'mystic' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, right: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const hoursContainerRef = useRef<HTMLDivElement>(null);
    const minutesContainerRef = useRef<HTMLDivElement>(null);

    // Parse current time with safe defaults to avoid NaN errors
    const timeParts = (value || '00:00').split(':').map(Number);
    const hours = isNaN(timeParts[0]) ? 0 : timeParts[0];
    const minutes = isNaN(timeParts[1]) ? 0 : timeParts[1];

    // Generate arrays
    const hoursArray = Array.from({ length: 24 }, (_, i) => i);
    const minutesArray = Array.from({ length: 60 }, (_, i) => i);

    // Close on click outside and update position
    useEffect(() => {


        const updatePosition = () => {
            if (containerRef.current && isOpen) {
                const rect = containerRef.current.getBoundingClientRect();
                const dropdownHeight = 256; // h-64
                const spaceBelow = window.innerHeight - rect.bottom;
                const shouldFlip = spaceBelow < dropdownHeight + 20; // 20px buffer

                setPosition({
                    top: shouldFlip
                        ? rect.top + window.scrollY - dropdownHeight - 8
                        : rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX,
                    right: rect.right + window.scrollX
                });
            }
        };

        if (isOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    // Body scroll lock
    useEffect(() => {
        if (isOpen) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isOpen]);

    // Handle outside click separately to account for Portal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isInsideContainer = containerRef.current?.contains(target);
            const isInsideDropdown = (target as Element).closest('.time-selector-dropdown');

            if (!isInsideContainer && !isInsideDropdown && isOpen) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Scroll to selected time on open
    useEffect(() => {
        if (isOpen) {
            const scrollToElement = (container: HTMLDivElement | null, value: number) => {
                if (!container) return;
                const selectedEl = container.querySelector(`[data-value="${value}"]`) as HTMLElement;
                if (selectedEl) {
                    // Use scrollTop instead of scrollIntoView to prevent main window scrolling
                    container.scrollTop = selectedEl.offsetTop - (container.clientHeight / 2) + (selectedEl.clientHeight / 2);
                }
            };

            // Small timeout to ensure render is complete (sometimes needed with Portal)
            setTimeout(() => {
                scrollToElement(hoursContainerRef.current, hours);
                scrollToElement(minutesContainerRef.current, minutes);
            }, 0);
        }
    }, [isOpen, hours, minutes]);

    const handleHourClick = (h: number) => {
        const hStr = h.toString().padStart(2, '0');
        const mStr = minutes.toString().padStart(2, '0');
        onChange(`${hStr}:${mStr}`);
    };

    const handleMinuteClick = (m: number) => {
        const hStr = hours.toString().padStart(2, '0');
        const mStr = m.toString().padStart(2, '0');
        onChange(`${hStr}:${mStr}`);
        setIsOpen(false);
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    w-full border rounded-xl pl-4 pr-10 h-[38px] text-white 
                    cursor-pointer transition-all flex items-center justify-between
                    ${isOpen ? 'border-gold-500 ring-1 ring-gold-500' : (theme === 'dark' ? 'border-white/10 hover:border-gold-500/50' : 'border-mystic-700 hover:border-gold-500/50')}
                    ${theme === 'dark' ? 'bg-[#0a0514]/60' : 'bg-mystic-900/50'}
                `}
            >
                <span className="font-mono text-sm tracking-wider">
                    {value}
                </span>
                <Clock
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-mystic-400 pointer-events-none transition-colors ${isOpen ? 'text-gold-400' : ''}`}
                    size={16}
                />
            </div>

            {isOpen && createPortal(
                <div
                    className={`time-selector-dropdown absolute z-[9999] border border-white/10 rounded-xl shadow-2xl backdrop-blur-xl flex overflow-hidden h-64 fade-in ${
                        theme === 'dark' ? 'bg-[#0c071d]/98' : 'bg-mystic-900'
                    }`}
                    style={{
                        top: `${position.top}px`,
                        left: `${position.right - 120}px`, // align to right edge (w-30 = 120px)
                        width: '7.5rem' // w-30
                    }}
                    dir="ltr"
                >
                    {/* Hours Column */}
                    <div ref={hoursContainerRef} className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-mystic-700 scrollbar-track-transparent border-r border-white/5 relative">
                        <div className="p-2 space-y-1">
                            {hoursArray.map((h) => (
                                <button
                                    key={h}
                                    data-value={h}
                                    onClick={() => handleHourClick(h)}
                                    className={`
                                        w-full text-center py-2 rounded-lg text-sm font-mono transition-colors
                                        ${h === hours
                                            ? 'bg-gold-500 text-mystic-950 font-bold'
                                            : 'text-mystic-300 hover:bg-white/5 hover:text-white'
                                        }
                                    `}
                                >
                                    {h.toString().padStart(2, '0')}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Minutes Column */}
                    <div ref={minutesContainerRef} className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-mystic-700 scrollbar-track-transparent relative">
                        <div className="p-2 space-y-1">
                            {minutesArray.map((m) => (
                                <button
                                    key={m}
                                    data-value={m}
                                    onClick={() => handleMinuteClick(m)}
                                    className={`
                                        w-full text-center py-2 rounded-lg text-sm font-mono transition-colors
                                        ${m === minutes
                                            ? 'bg-gold-500 text-mystic-950 font-bold'
                                            : 'text-mystic-300 hover:bg-white/5 hover:text-white'
                                        }
                                    `}
                                >
                                    {m.toString().padStart(2, '0')}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
